import { useState } from 'react';
import { useAdminInventory } from '@/api/ads/hooks';
import { ResourceGate } from '@/features/ResourceGate';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardBody } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { Table, Th, Td, EmptyRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { npr, pct, toISODate } from '@/lib/format';

export function InventoryPage() {
  const today = new Date();
  const in14 = new Date(Date.now() + 14 * 86400000);
  const [start, setStart] = useState(toISODate(today));
  const [end, setEnd] = useState(toISODate(in14));
  const { data, isLoading } = useAdminInventory({ start, end });

  return (
    <ResourceGate slug="ads.inventory">
      <PageHeader
        title="Inventory"
        description="Slot fill against rate-card capacity for a date window."
        action={
          <div className="flex items-end gap-2">
            <div>
              <Label>Start</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
            </div>
            <div>
              <Label>End</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
            </div>
          </div>
        }
      />
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6">
              <Spinner label="Loading inventory…" />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Daypart</Th>
                  <Th>Capacity</Th>
                  <Th>Taken</Th>
                  <Th>Available</Th>
                  <Th>Fill</Th>
                  <Th>Unit price</Th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows.length ?? 0) === 0 && <EmptyRow colSpan={7}>No active rate entries.</EmptyRow>}
                {data?.rows.map((r, i) => (
                  <tr key={`${r.product_code}-${r.daypart}-${i}`}>
                    <Td className="font-medium">{r.product}</Td>
                    <Td className="text-muted-foreground">{r.daypart}</Td>
                    <Td>{r.capacity}</Td>
                    <Td>{r.taken}</Td>
                    <Td>{r.available}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${Math.min(100, r.fill_pct)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{pct(r.fill_pct)}</span>
                      </div>
                    </Td>
                    <Td className="text-muted-foreground">{npr(r.unit_price)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </ResourceGate>
  );
}
